import React, { useState, useEffect, useCallback } from 'react';
import { SearchIcon } from './icons/SearchIcon';
import { RefreshIcon } from './icons/RefreshIcon';
import CloseIcon from './icons/CloseIcon';
import ImageCard from './ImageCard';
import { fetchUnsplashImages } from '../utils/unsplashUtils';
import {
    BackgroundPickerContainer,
    SearchContainer,
    SearchInput,
    IconButton,
    ImageGrid,
    LoadingSpinner,
    TabContainer,
    TabButton,
    ColorOption,
    ColorGrid,
    FontColorContainer,
    FontColorLabel,
    FontColorSelect,
    HeaderContainer,
    PickerTitle
} from '../styles/BackgroundPicker.styles';

const DEFAULT_SEARCH_QUERY = 'nature,landscape';
const BACKGROUND_TYPES = {
    IMAGE: 'image',
    COLOR: 'color'
};
const PREDEFINED_COLORS = [
    // Material Design Colors
    { name: 'Blue Gray', value: '#607D8B' },
    { name: 'Gray', value: '#9E9E9E' },
    { name: 'Deep Blue', value: '#1976D2' },
    { name: 'Indigo', value: '#3F51B5' },
    { name: 'Deep Purple', value: '#673AB7' },
    { name: 'Purple', value: '#9C27B0' },
    { name: 'Pink', value: '#E91E63' },
    { name: 'Red', value: '#F44336' },
    { name: 'Deep Orange', value: '#FF5722' },
    { name: 'Orange', value: '#FF9800' },
    { name: 'Amber', value: '#FFC107' },
    { name: 'Yellow', value: '#FFEB3B' },
    { name: 'Lime', value: '#CDDC39' },
    { name: 'Light Green', value: '#8BC34A' },
    { name: 'Green', value: '#4CAF50' },
    { name: 'Teal', value: '#009688' },
    { name: 'Cyan', value: '#00BCD4' },
    { name: 'Light Blue', value: '#03A9F4' },

    // Dark Theme Colors
    { name: 'Dark Gray', value: '#424242' },
    { name: 'Dark Slate', value: '#2D3436' },
    { name: 'Midnight Blue', value: '#2C3E50' },
    { name: 'Dark Purple', value: '#4A148C' },
    { name: 'Dark Red', value: '#B71C1C' },

    // Light Theme Colors
    { name: 'Pearl White', value: '#F5F5F5' },
    { name: 'Soft Beige', value: '#F5F5DC' },
    { name: 'Light Gray', value: '#E0E0E0' },
    { name: 'Pale Blue', value: '#BBDEFB' },
    { name: 'Mint Green', value: '#C8E6C9' }
];
const FONT_COLORS = [
    { name: 'Black', value: '#000000' },
    { name: 'White', value: '#FFFFFF' },
    { name: 'Dark Gray', value: '#333333' },
    { name: 'Light Gray', value: '#CCCCCC' }
];

const BackgroundPicker = ({ currentBackground, projectTitle, onSelectBackground, onClose }) => {
    const [images, setImages] = useState([]);
    const [loading, setLoading] = useState(false);
    const [searchQuery, setSearchQuery] = useState(projectTitle || DEFAULT_SEARCH_QUERY);
    const [searchInput, setSearchInput] = useState('');
    const [activeTab, setActiveTab] = useState(BACKGROUND_TYPES.IMAGE);
    const [selectedFontColor, setSelectedFontColor] = useState(
        currentBackground?.fontColor || FONT_COLORS[1].value // Default to White
    );
    const [customColor, setCustomColor] = useState('#000000');

    const fetchImages = useCallback(async (query = searchQuery) => {
        setLoading(true);
        try {
            const data = await fetchUnsplashImages(query);
            setImages(data);
        } catch (error) {
            console.error('Error fetching images:', error);
        } finally {
            setLoading(false);
        }
    }, [searchQuery]);

    useEffect(() => {
        if (activeTab === BACKGROUND_TYPES.IMAGE) {
            fetchImages();
        }
    }, [fetchImages, activeTab]);

    const handleRefresh = (e) => {
        e.stopPropagation();
        fetchImages();
    };

    const handleSearch = (e) => {
        e.preventDefault();
        const trimmedInput = searchInput.trim();
        if (trimmedInput) {
            setSearchQuery(trimmedInput);
            fetchImages(trimmedInput);
        }
    };

    const handleInputChange = (e) => {
        setSearchInput(e.target.value);
    };

    const handleTabChange = (tab) => {
        setActiveTab(tab);
    };

    const handleColorSelect = (color) => {
        const backgroundData = {
            type: BACKGROUND_TYPES.COLOR,
            color: color,
            fontColor: selectedFontColor
        };
        console.log('Selecting Color Background:', backgroundData);
        onSelectBackground(backgroundData);
    };

    const handleImageSelect = (image) => {
        const backgroundData = {
            ...image,
            type: BACKGROUND_TYPES.IMAGE,
            fontColor: selectedFontColor
        };
        console.log('Selecting Image Background:', backgroundData);
        onSelectBackground(backgroundData);
    };

    const handleFontColorChange = (e) => {
        setSelectedFontColor(e.target.value);
    };

    const handleClose = (e) => {
        e.preventDefault();
        onClose();
    };

    const renderContent = () => {
        if (activeTab === BACKGROUND_TYPES.IMAGE) {
            if (loading) {
                return <LoadingSpinner>Loading...</LoadingSpinner>;
            }
            return images.map(image => (
                <ImageCard
                    key={image.id}
                    image={image}
                    onSelect={handleImageSelect}
                />
            ));
        } else {
            return (
                <>
                    {PREDEFINED_COLORS.map(color => (
                        <ColorOption
                            key={color.value}
                            color={color.value}
                            onClick={() => handleColorSelect(color.value)}
                            title={color.name}
                        />
                    ))}
                    <ColorOption
                        key="custom"
                        color={customColor}
                        onClick={() => handleColorSelect(customColor)}
                        title="Custom Color"
                        style={{ position: 'relative' }}
                    >
                        <input
                            type="color"
                            value={customColor}
                            onChange={(e) => setCustomColor(e.target.value)}
                            style={{
                                position: 'absolute',
                                top: 0,
                                left: 0,
                                width: '100%',
                                height: '100%',
                                opacity: 0,
                                cursor: 'pointer'
                            }}
                            title="Choose custom color"
                        />
                        <div style={{
                            position: 'absolute',
                            top: '50%',
                            left: '50%',
                            transform: 'translate(-50%, -50%)',
                            color: '#fff',
                            textShadow: '0 1px 2px rgba(0,0,0,0.6)',
                            fontSize: '20px'
                        }}>
                            +
                        </div>
                    </ColorOption>
                </>
            );
        }
    };

    return (
        <BackgroundPickerContainer>
            <HeaderContainer>
                <PickerTitle>Background Settings</PickerTitle>
                <IconButton onClick={handleClose} title="Back to Project">
                    <CloseIcon />
                </IconButton>
            </HeaderContainer>

            <TabContainer>
                <TabButton
                    active={activeTab === BACKGROUND_TYPES.IMAGE}
                    onClick={() => handleTabChange(BACKGROUND_TYPES.IMAGE)}
                >
                    Images
                </TabButton>
                <TabButton
                    active={activeTab === BACKGROUND_TYPES.COLOR}
                    onClick={() => handleTabChange(BACKGROUND_TYPES.COLOR)}
                >
                    Colors
                </TabButton>
            </TabContainer>

            {activeTab === BACKGROUND_TYPES.IMAGE && (
                <SearchContainer onSubmit={handleSearch}>
                    <SearchInput
                        type="text"
                        placeholder="Search backgrounds..."
                        value={searchInput}
                        onChange={handleInputChange}
                    />
                    <IconButton type="submit" title="Search">
                        <SearchIcon />
                    </IconButton>
                    <IconButton
                        type="button"
                        onClick={handleRefresh}
                        title="Refresh backgrounds"
                    >
                        <RefreshIcon />
                    </IconButton>
                </SearchContainer>
            )}

            <FontColorContainer>
                <FontColorLabel>Font Color:</FontColorLabel>
                <FontColorSelect
                    value={selectedFontColor}
                    onChange={handleFontColorChange}
                >
                    {FONT_COLORS.map(color => (
                        <option key={color.value} value={color.value}>
                            {color.name}
                        </option>
                    ))}
                </FontColorSelect>
            </FontColorContainer>

            {activeTab === BACKGROUND_TYPES.IMAGE ? (
                <ImageGrid>
                    {renderContent()}
                </ImageGrid>
            ) : (
                <ColorGrid>
                    {renderContent()}
                </ColorGrid>
            )}
        </BackgroundPickerContainer>
    );
};

export default BackgroundPicker;
